import { useCallback, useState } from "react";
import { createPortal } from "react-dom";
import { useAuthUser } from "../contexts/AuthUserContext";
import { submitFeatureInterest } from "../utils/api/api";
import styles from "./ComingSoonModal.module.css";

export default function ComingSoonModal({ feature, onClose }) {
  const { authUser } = useAuthUser();
  const [email, setEmail] = useState(authUser?.email ?? "");
  const [closing, setClosing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleClose = useCallback(() => {
    setClosing(true);
    setTimeout(() => onClose?.(), 200);
  }, [onClose]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!email.trim()) return;
      setSubmitting(true);
      setError(null);
      try {
        await submitFeatureInterest({ email: email.trim(), feature });
        setSubmitted(true);
      } catch {
        setError("Something went wrong. Please try again.");
      } finally {
        setSubmitting(false);
      }
    },
    [email, feature]
  );

  const modalNode = (
    <div
      className={`${styles.overlay} ${closing ? styles.overlayOut : ""}`}
      onClick={handleClose}
    >
      <div
        className={`${styles.modal} ${closing ? styles.modalOut : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        <button className={styles.closeBtn} onClick={handleClose} aria-label="Close">
          ✕
        </button>

        <div className={styles.eyebrow}>Coming Soon</div>
        {/* <div className={styles.icon}>🚀</div> */}

        <h2 className={styles.title}>{feature}</h2>
        <p className={styles.body}>
          We're working on this feature. Drop your email and we'll let you know the moment it goes live.
        </p>

        {submitted ? (
          <div className={styles.success}>
            <div className={styles.successIcon}>✓</div>
            <p className={styles.successText}>You're on the list! We'll reach out when {feature} launches.</p>
            <button className={styles.doneBtn} onClick={handleClose}>
              Got it
            </button>
          </div>
        ) : (
          <form className={styles.form} onSubmit={handleSubmit}>
            <input
              type="email"
              className={styles.input}
              placeholder="your@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus={!email}
            />
            {error && <p className={styles.error}>{error}</p>}
            <button
              type="submit"
              className={styles.submitBtn}
              disabled={submitting || !email.trim()}
            >
              {submitting ? "Submitting…" : "Notify me"}
            </button>
            <button type="button" className={styles.cancelBtn} onClick={handleClose}>
              Maybe later
            </button>
          </form>
        )}
      </div>
    </div>
  );

  if (typeof document === "undefined") return modalNode;
  return createPortal(modalNode, document.body);
}
